import { NgClass } from "@angular/common";
import { ChangeDetectionStrategy, Component, computed, inject, input, model, output } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { StrategyDspAudiencePreset } from "@front/m19-api-client";
import { DspAudiencesConfig } from "@front/m19-services";
import {
  IButtonDirective,
  IHintDirective,
  IIconComponent,
  ILabelComponent,
  IRadioComponent,
  IRadioGroupComponent,
  ITooltipDirective,
} from "@front/m19-ui";
import { TranslocoDirective } from "@jsverse/transloco";
import { DspSmartAudiencesService } from "@m19-board/dsp/dsp-smart-audiences-service/dsp-smart-audiences.service";

@Component({
  selector: "app-dsp-smart-audience-selector",
  imports: [
    IHintDirective,
    NgClass,
    TranslocoDirective,
    IRadioComponent,
    ILabelComponent,
    IRadioGroupComponent,
    IIconComponent,
    ITooltipDirective,
    IButtonDirective,
  ],
  template: `<IRadioGroup
    name="smartAudience"
    class="grid grid-cols-2 gap-4"
    *transloco="let t"
    [value]="selectedPreset()"
    (valueChange)="select($event)"
  >
    @for (preset of availablePresets(); track preset.title) {
      @let isSelected = selectedPreset() === preset.preset;
      @let isDisabled = disabled() || preset.disabled;
      <div
        class="flex"
        [class.cursor-not-allowed]="isDisabled"
        [iTooltip]="preset.disabled ? t('dsp-audience-preset.amc_not_available_yet') : undefined"
      >
        <label
          class="flex grow"
          [class.cursor-pointer]="!isDisabled"
          [class.pointer-events-none]="isDisabled"
          [class.opacity-60]="isDisabled"
          iLabel
          [for]="preset.preset"
          type="card"
          (click)="handlePresetClick($event, preset.preset)"
        >
          <div class="flex gap-4">
            <div class="flex items-center text-xl" [ngClass]="isSelected ? 'text-main' : 'text-muted-foreground'">
              <IIcon [name]="preset.icon" />
            </div>
            <div>
              <div class="type-body-medium">
                {{ t("dsp-audience-preset." + preset.title) }}
              </div>
              @if (
                preset.preset === StrategyDspAudiencePreset.MANUAL &&
                selectedPreset() === StrategyDspAudiencePreset.MANUAL &&
                existingAudienceConfig()
              ) {
                <div class="text-muted-foreground mb-2">
                  {{
                    t("dsp-audiences-selector.x_audiences", {
                      count: totalAudiences(),
                    })
                  }}
                  ·
                  {{
                    t("dsp-audiences-selector.x_blacklist", {
                      count: totalBlacklistedAudiences(),
                    })
                  }}
                </div>
                <button iButton variant="secondary" [disabled]="disabled()" (click)="onManageAudiences($event)">
                  {{ t("dsp-audiences-selector.manage_audiences") }}
                </button>
              } @else {
                <div class="text-muted-foreground">{{ t("dsp-audience-preset." + preset.description) }}</div>
              }
              @if (preset.warning && isSelected) {
                <div iHint variant="warning" class="mt-2">{{ t("dsp-audience-preset." + preset.warning) }}</div>
              }
            </div>
          </div>
          @if (preset.disabled) {
            <div class="text-muted-foreground flex items-center text-xl">
              <IIcon name="lock" />
            </div>
          } @else {
            <IRadio
              [id]="preset.preset"
              [value]="preset.preset"
              size="lg"
              [class.pointer-events-none]="preset.preset === StrategyDspAudiencePreset.MANUAL"
            />
          }
        </label>
      </div>
    }
  </IRadioGroup>`,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DspSmartAudienceSelectorComponent {
  private readonly smartAudiencesService = inject(DspSmartAudiencesService);
  readonly existingAudienceConfig = input<DspAudiencesConfig | undefined>();
  readonly disabled = input(false);
  readonly StrategyDspAudiencePreset = StrategyDspAudiencePreset;

  readonly selectedPreset = model.required<StrategyDspAudiencePreset>();
  readonly manualAudienceSelected = output<void>();
  readonly manageAudiencesSelected = output<void>();

  private readonly amcPresetsAvailable = toSignal(this.smartAudiencesService.amcPresetsAvailable$, {
    initialValue: false,
  });

  readonly availablePresets = computed(() =>
    this.smartAudiencesService.getAvailablePresets().map((preset) => ({
      ...preset,
      disabled: !!preset.amc && !this.amcPresetsAvailable(),
    })),
  );

  readonly totalAudiences = computed(() => this.existingAudienceConfig()?.whitelistAudiences?.flat().length ?? 0);
  readonly totalBlacklistedAudiences = computed(
    () => this.existingAudienceConfig()?.blacklistAudiences?.flat().length ?? 0,
  );

  handlePresetClick(event: MouseEvent, preset: StrategyDspAudiencePreset) {
    if (this.disabled()) {
      return;
    }
    if (preset === StrategyDspAudiencePreset.MANUAL) {
      event.preventDefault();
      this.manualAudienceSelected.emit();
    }
  }

  onManageAudiences(event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();
    if (this.disabled()) {
      return;
    }
    this.manageAudiencesSelected.emit();
  }

  select(preset?: StrategyDspAudiencePreset) {
    if (this.disabled() || !preset) {
      return;
    }
    this.selectedPreset.set(preset);
  }
}
